import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Badge, EmptyState } from "@/components/Common";
import { STATO_CORSO, MACRO_TIPOLOGIA, fmtDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, Search, Loader2, Download } from "lucide-react";
import { toast } from "sonner";

export default function Corsi() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [corsi, setCorsi] = useState(null);
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [filterStato, setFilterStato] = useState("tutti");
  const [filterTipo, setFilterTipo] = useState("tutti");
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    titolo: "", macro_tipologia: "fse_gol", ore_previste: "", data_avvio: "",
    data_termine: "", responsabile: "", stato: "bozza",
  });

  useEffect(() => {
    let active = true;
    const term = q.trim();
    const t = setTimeout(() => {
      api
        .get("/corsi", { params: term ? { q: term } : {} })
        .then(({ data }) => { if (active) setCorsi(data); })
        .catch(() => { if (active) setCorsi([]); });
    }, 300);
    return () => { active = false; clearTimeout(t); };
  }, [q]);

  useEffect(() => { setQ(searchParams.get("q") || ""); }, [searchParams]);

  const filtered = (corsi || []).filter((c) =>
    (filterStato === "tutti" || c.stato === filterStato) &&
    (filterTipo === "tutti" || c.macro_tipologia === filterTipo)
  );

  const createCorso = async () => {
    if (!form.titolo || !form.ore_previste) {
      toast.error("Titolo e numero ore sono obbligatori");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, ore_previste: parseFloat(form.ore_previste), azienda_ids: [] };
      const { data } = await api.post("/corsi", payload);
      toast.success(`Corso ${data.codice} creato`);
      setOpen(false);
      navigate(`/corsi/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const exportCsv = () => {
    const rows = [["Codice", "Titolo", "Tipologia", "Stato", "Ore", "Avvio", "Termine"]];
    filtered.forEach((c) => rows.push([
      c.codice, c.titolo, MACRO_TIPOLOGIA[c.macro_tipologia]?.label || c.macro_tipologia,
      STATO_CORSO[c.stato]?.label || c.stato, c.ore_previste, c.data_avvio, c.data_termine,
    ]));
    const csv = rows.map((r) => r.map((x) => `"${x ?? ""}"`).join(";")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "corsi_indar.csv"; a.click();
    toast.success("Export CSV generato");
  };

  return (
    <div data-testid="corsi-page">
      <PageHeader title="Corsi" subtitle="Tutte le edizioni e i percorsi di finanziamento">
        <Button variant="outline" onClick={exportCsv} data-testid="export-corsi-btn">
          <Download className="mr-1.5 h-4 w-4" /> Export
        </Button>
        {user?.can_write && (
          <Button onClick={() => setOpen(true)} data-testid="new-corso-btn" className="bg-[#E11D48] hover:bg-[#BE123C]">
            <Plus className="mr-1.5 h-4 w-4" /> Nuovo Corso
          </Button>
        )}
      </PageHeader>

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative max-w-xs flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per codice o titolo..." className="pl-9" data-testid="corsi-search" />
        </div>
        <Select value={filterStato} onValueChange={setFilterStato}>
          <SelectTrigger className="w-full sm:w-48" data-testid="filter-stato"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="tutti">Tutti gli stati</SelectItem>
            {Object.entries(STATO_CORSO).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterTipo} onValueChange={setFilterTipo}>
          <SelectTrigger className="w-full sm:w-48" data-testid="filter-tipo"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="tutti">Tutte le tipologie</SelectItem>
            {Object.entries(MACRO_TIPOLOGIA).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-xl border bg-white overflow-hidden">
        {corsi === null ? (
          <div className="flex justify-center py-20 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <EmptyState title="Nessun corso trovato" hint="Modifica i filtri o crea un nuovo corso." />
        ) : (
          <>
          <div className="divide-y sm:hidden" data-testid="corsi-cards">
            {filtered.map((c) => {
              const st = STATO_CORSO[c.stato] || STATO_CORSO.bozza;
              const tp = MACRO_TIPOLOGIA[c.macro_tipologia] || {};
              return (
                <button key={c.id} type="button" onClick={() => navigate(`/corsi/${c.id}`)} className="block w-full p-4 text-left active:bg-slate-50" data-testid="corso-card">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-xs font-semibold text-[#E11D48]">{c.codice}</span>
                    <Badge label={st.label} cls={st.cls} />
                  </div>
                  <div className="mt-1 font-medium text-slate-900">{c.titolo}</div>
                  <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                    <Badge label={tp.label || c.macro_tipologia} cls={tp.cls || ""} />
                    <span>{c.ore_previste}h</span>
                    <span>Avvio {fmtDate(c.data_avvio)}</span>
                  </div>
                </button>
              );
            })}
          </div>
          <div className="hidden overflow-x-auto sm:block">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Codice</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Titolo</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Tipologia</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Ore</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Avvio</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Stato</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((c) => {
                  const st = STATO_CORSO[c.stato] || STATO_CORSO.bozza;
                  const tp = MACRO_TIPOLOGIA[c.macro_tipologia] || {};
                  return (
                    <TableRow key={c.id} className="cursor-pointer" onClick={() => navigate(`/corsi/${c.id}`)} data-testid="corso-row">
                      <TableCell className="font-mono text-xs font-medium text-[#E11D48]">{c.codice}</TableCell>
                      <TableCell className="font-medium text-slate-900">{c.titolo}</TableCell>
                      <TableCell><Badge label={tp.label || c.macro_tipologia} cls={tp.cls || ""} /></TableCell>
                      <TableCell className="text-sm">{c.ore_previste}h</TableCell>
                      <TableCell className="text-sm">{fmtDate(c.data_avvio)}</TableCell>
                      <TableCell><Badge label={st.label} cls={st.cls} /></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          </>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Nuovo Corso — Creazione Guidata</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            <div className="sm:col-span-2">
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Macro-tipologia</Label>
              <Select value={form.macro_tipologia} onValueChange={(v) => setForm({ ...form, macro_tipologia: v })}>
                <SelectTrigger className="mt-1" data-testid="wizard-tipologia"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(MACRO_TIPOLOGIA).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Titolo</Label>
              <Input value={form.titolo} onChange={(e) => setForm({ ...form, titolo: e.target.value })} className="mt-1" data-testid="wizard-titolo" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Ore previste</Label>
              <Input type="number" value={form.ore_previste} onChange={(e) => setForm({ ...form, ore_previste: e.target.value })} className="mt-1" data-testid="wizard-ore" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Responsabile</Label>
              <Input value={form.responsabile} onChange={(e) => setForm({ ...form, responsabile: e.target.value })} className="mt-1" data-testid="wizard-responsabile" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Data avvio</Label>
              <Input type="date" value={form.data_avvio} onChange={(e) => setForm({ ...form, data_avvio: e.target.value })} className="mt-1" data-testid="wizard-avvio" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Data termine</Label>
              <Input type="date" value={form.data_termine} onChange={(e) => setForm({ ...form, data_termine: e.target.value })} className="mt-1" data-testid="wizard-termine" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={createCorso} disabled={saving} className="bg-[#E11D48] hover:bg-[#BE123C]" data-testid="wizard-save">
              {saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />} Crea Corso
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
